import { extend } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { EventSystem } from "@dhx/ts-common/events";
import { toNode } from "@dhx/ts-common/html";
import { IHandlers } from "@dhx/ts-common/types";
import { View, toViewLike } from "@dhx/ts-common/view";
import { Layout } from "@dhx/ts-layout";
import { Toolbar } from "@dhx/ts-toolbar";
import { defaultBlocks } from "./constants";
import { listenCompositionEvents } from "./helpers/composition";
import { LinkEditor } from "./helpers/link";
import { getLinkRefer, getTextHash } from "./helpers/other";
import { createScrollHelper, IScrollHelper } from "./helpers/scroll";
import { getStats } from "./helpers/statistics";
import { blockquoteWrapper, blockStyleToTag, calcBlockStyle, calcTextNodeStyle } from "./helpers/styles";
import { RichTextToolbarHelper } from "./helpers/toolbar";
import { keyToOperation } from "./keyhandler";
import { Editor } from "./models/Editor";
import { TextNode } from "./models/TextNode";
import {
	Action,
	IEditorAPI,
	IRichText,
	IRichTextConfig,
	IRichTextEventSystem,
	ISelectionState,
	RichTextEvents,
	RTEToolbarBlock,
} from "./types";

export class Richtext extends View implements IRichText {
	public events: IRichTextEventSystem;
	public config: IRichTextConfig;

	public toolbar: Toolbar;

	private _editor: Editor;

	private _layout: Layout;

	private _handlers: IHandlers;

	private _initContainer: HTMLElement;
	private _toolbarHelper: RichTextToolbarHelper;
	private _scrollHelper: IScrollHelper;
	private _linkEditor: LinkEditor;

	private _restoreFocusTimeout: any;

	private _compositionEventsDestructor: () => void;

	private _editorView: any;

	private _composition: {
		active: boolean;
		initSelection: ISelectionState;
	};

	constructor(container: HTMLElement | string, config: any) {
		super(
			container,
			extend(
				{
					mode: "classic",
					toolbarBlocks: [
						RTEToolbarBlock.undo,
						RTEToolbarBlock.style,
						RTEToolbarBlock.decoration,
						RTEToolbarBlock.color,
						RTEToolbarBlock.align,
						RTEToolbarBlock.link,
						// RTEToolbarBlock.clear,
						// RTEToolbarBlock.fullscreen
					],
				},
				config
			)
		);

		const defaultBlockIndex = this.config.toolbarBlocks.indexOf(RTEToolbarBlock.default);
		if (defaultBlockIndex !== -1) {
			this.config.toolbarBlocks.splice(defaultBlockIndex, 1, ...defaultBlocks);
		}

		this._initContainer = toNode(container);

		this.events = new EventSystem(this) as IRichTextEventSystem;

		const getRootView = () => this._editorView.getRootView();

		this._composition = {
			active: false,
			initSelection: null,
		};
		this._compositionEventsDestructor = listenCompositionEvents(this.events, () =>
			this._editor.selection.isActive()
		);

		this._editor = new Editor({
			events: this.events,
			uid: this._uid,
			getRootView,
			repaint: () => this._editorView.paint(),
		});

		this._toolbarHelper = new RichTextToolbarHelper(this, this._editor);
		this.toolbar = this._toolbarHelper.toolbar;
		this._linkEditor = new LinkEditor(null, {
			events: this.events,
			editor: this._editor,
			getRootView,
		});

		this._initHandlers();
		this._initUI(this._initContainer);
		this._initEvents();
	}
	getRootView() {
		return this._layout.getRootView();
	}
	setValue(value: string, mode: "html" | "markdown" = "html") {
		this.events.fire(RichTextEvents.action, [Action.parse, { value, mode }, false]);
	}
	getValue(mode: "html" | "text" | "markdown" = "html") {
		return this._editor.serialize(mode);
	}
	getStats(selection?: boolean): { [key: string]: number } {
		const text = selection ? this._editor.selection.getSelectedText() : this.getValue("text");
		return getStats(text, this.config.customStats);
	}
	destructor() {
		this._compositionEventsDestructor();
		this._editor.selection.destructor();
		this.toolbar.destructor();
		this.unmount();
	}
	fullScreen() {
		if (this.toolbar.data.exists("fullscreen")) {
			this.toolbar.data.update("fullscreen", {
				icon: "dxi dxi-arrow-collapse",
				$fullscreen: true,
			});
		}

		document.body.classList.add("dhx_richtext--fullscreen", "dhx_widget--fullscreen");
		const view = this.getRootView();
		view.mount(document.body);
	}
	exitFullScreen() {
		if (this.toolbar.data.exists("fullscreen")) {
			this.toolbar.data.update("fullscreen", {
				icon: "dxi dxi-arrow-expand",
				$fullscreen: false,
			});
		}

		document.body.classList.remove("dhx_richtext--fullscreen", "dhx_widget--fullscreen");
		const view = this.getRootView();
		view.mount(this._initContainer);
	}
	getEditorAPI(): IEditorAPI {
		return {
			getSelection: () => this._editor.selection.get(),
			setSelection: selection => {
				this._editor.selection.set(selection);
				this._restoreFocus();
			},
			getPosition: selection => this._editor.selection.getPosition(selection),
			getModel: () =>
				this._editor.blocks.map(block => ({
					style: { ...block.style },
					textNodes: block.textNodes.map(textNode => ({
						style: { ...textNode.style },
						text: textNode.text,
					})),
				})),
			setModel: (blocks, selection?) => {
				this.events.fire(RichTextEvents.action, [
					Action.parse,
					{ value: blocks, mode: "inner" },
					false,
				]);
				if (selection) {
					this._editor.selection.set(selection);
				}
			},
			add: (config, selection?) => {
				if (selection) {
					this._editor.selection.set(selection);
				}
				this.events.fire(RichTextEvents.action, [Action.innerAdd, config, true]);
			},
			remove: (selection?) => {
				if (selection) {
					this._editor.selection.set(selection);
				}
				this.events.fire(RichTextEvents.action, [Action.remove, true, true]);
			},
			update: (config, selection?) => {
				if (selection) {
					this._editor.selection.set(selection);
				}
				this.events.fire(RichTextEvents.action, [
					Action.update,
					{
						modifier: config.modifier,
						modifierValue: config.value,
					},
				]);
			},
		};
	}
	private _initUI(container) {
		const layout = (this._layout = new Layout(container, {
			css: "dhx_richtext" + " dhx_richtext--mode_" + this.config.mode,
			cols: [
				{
					css: "dhx_toolbar__height-holder",
					rows: [
						{
							id: "toolbar",
							on: { click: () => this._restoreFocus() },
							css: "dhx_richtext__toolbar-holder",
							height: "content",
						},
						{ id: "editor", css: "dhx_richtext__editor-holder" },
					],
				},
			],
		}));

		this._editorView = toViewLike(
			create({
				render: () => this._draw(),
				hooks: {
					didRedraw: () => {
						if (this._editor.selection.isActive()) {
							const isUpdated = this._editor.selection.update();
							if (!isUpdated) {
								this._restoreFocus();
							}
						}
					},
				},
			})
		);

		layout.getCell("editor").attach(this._editorView);
		layout.getCell("toolbar").attach(this.toolbar);
	}
	private _initHandlers() {
		this._handlers = {
			onkeydown: (e: KeyboardEvent) => {
				if (
					this._composition.active ||
					(this._linkEditor.isVisible() && !this._editor.selection.isActive())
				) {
					return;
				}
				const operation = keyToOperation(e);
				if (!operation) {
					return;
				}
				this.events.fire(RichTextEvents.action, [operation.action, operation.data]);
			},
			onpaste: (e: ClipboardEvent) => {
				this.events.fire(RichTextEvents.action, [Action.paste, e, false]);
			},
			oncut: (e: ClipboardEvent) => {
				this.events.fire(RichTextEvents.action, [Action.cut, e, false]);
			},
			oncopy: (e: ClipboardEvent) => {
				this.events.fire(RichTextEvents.action, [Action.copy, e, false]);
			},
			ondrop: e => {
				e.preventDefault();
			},
			ondragstart: e => {
				e.preventDefault();
			},
		};
	}
	private _initEvents() {
		this.events.on(RichTextEvents.selectionRefresh, () => this._scrollHelper.update());
		this.events.on(RichTextEvents.action, (action, data, restoreFocus) => {
			const isChanged = this._editor.manager.execute({ action, data });
			if (isChanged) {
				this.events.fire(RichTextEvents.change, [
					action,
					this._editor.manager.canUndo(),
					this._editor.manager.canRedo(),
				]);
				this._editorView.paint();
			}
			if (restoreFocus) {
				this._restoreFocus();
			}
		});

		this.events.on(RichTextEvents.compositionStart, () => {
			this._composition.active = true;
			this._composition.initSelection = this._editor.selection.get();
		});
		this.events.on(RichTextEvents.compositionEnd, data => {
			this._editor.selection.set(this._composition.initSelection);

			this.events.fire(RichTextEvents.action, [Action.add, data, true]);
			this._composition.active = false;
			this._composition.initSelection = null;
		});
	}
	private _restoreFocus() {
		if (this._restoreFocusTimeout) {
			clearTimeout(this._restoreFocusTimeout);
		}
		this._restoreFocusTimeout = setTimeout(() => {
			this._scrollHelper.saveScrollTop();
			this._editorView.getRootView().refs.editor.el.focus();
			this._scrollHelper.restoreScrollTop();
			const isUpdated = this._editor.selection.refresh();
			this._restoreFocusTimeout = null;
			if (!isUpdated) {
				this._restoreFocus();
			}
		}, 25);
	}
	private _draw() {
		const currentLinkRef = this._linkEditor.getCurrentLinkRef();
		const linkRefer = getLinkRefer();
		let currentLink = null;
		let currentLinkArr = [];

		return el(
			".dhx_richtext__overflow-wrapper",
			{
				_hooks: this.config.mode === "document" && {
					didInsert: node => {
						this._scrollHelper = createScrollHelper(node.el);
					},
				},
			},
			[
				el(
					".dhx_richtext__editor.dhx_richtext-editor",
					{
						dhx_widget_id: this._uid,
						_ref: "editor",
						contentEditable: "true",
						role: "textbox",
						"data-gramm": "false",
						spellcheck: "false",
						_hooks: this.config.mode !== "document" && {
							didInsert: node => {
								this._scrollHelper = createScrollHelper(node.el);
							},
						},
						...this._handlers,
					},
					this._editor.blocks.map((block, blockIndex) => {
						if (currentLinkArr.length) {
							currentLinkArr = [];
							currentLink = null;
						}
						return blockquoteWrapper(block.style, blockIndex)(
							blockStyleToTag(block.style),
							{
								style: calcBlockStyle(block.style),
								dhx_offset: blockIndex,
							},
							block.textNodes.map((textNode, textIndex) => {
								const hash = getTextHash(blockIndex, textIndex);
								const isLastBlockNode = textIndex === block.textNodes.length - 1;

								if (textNode.style.link) {
									if (currentLink !== textNode.style.link) {
										currentLinkArr = [
											this._renderTextNode(textNode, hash, isLastBlockNode),
										];
										currentLink = textNode.style.link;
										const linkRef = linkRefer();

										return el(
											"a",
											{
												href: textNode.style.link,
												class: linkRef === currentLinkRef ? "dhx--link_edited" : "",
												_ref: linkRef,
											},
											currentLinkArr
										);
									} else {
										currentLinkArr.push(
											this._renderTextNode(textNode, hash, isLastBlockNode)
										);
										return;
									}
								}
								currentLink = textNode.style.link;
								return this._renderTextNode(textNode, hash, isLastBlockNode);
							})
						);
					})
				),
			]
		);
	}
	private _renderTextNode(textNode: TextNode, hash: string, isLastBlockNode: boolean) {
		const text = textNode.text;
		const lastSymbolIsGap = text.endsWith("\n") && isLastBlockNode;
		return el(
			"span",
			{
				_ref: hash,
				dhx_offset: hash,
				style: calcTextNodeStyle(textNode.style),
			},
			text.length === 0 ? [el("br")] : lastSymbolIsGap ? [text + "\n"] : text
		);
	}
}
